import React, { useMemo } from 'react';
import { Breadcrumb } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { menuItems } from '../menuConfig';
import { useLocale } from '../context/LocaleContext';
import { useTheme } from '../context/ThemeContext';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

const BreadcrumbNav: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLocale();
  const { isDark } = useTheme();
  const path = location.pathname;
  const hidden = path.startsWith('/finance/quote-generator') || path === '/login' || path === '/quote-view';

  const breadcrumbs = useMemo(() => {
    const findMenuPath = (items: typeof menuItems, targetPath: string): BreadcrumbItem[] | null => {
      for (const item of items) {
        if (item.path === targetPath) {
          return [{ label: t(item.label), path: item.path }];
        }
        if (item.children) {
          const childPath = findMenuPath(item.children, targetPath);
          if (childPath) {
            return [{ label: t(item.label) }, ...childPath];
          }
        }
      }
      return null;
    };
    const items: BreadcrumbItem[] = [];

    if (path === '/') {
      items.push({ label: t('工作台'), path: '/' });
    } else {
      const menuPath = findMenuPath(menuItems, path);
      if (menuPath) {
        items.push(...menuPath);
      } else {
        if (path.startsWith('/orders/') && path !== '/orders/create') {
          items.push({ label: t('订单中心') });
          items.push({ label: t('所有订单'), path: '/orders' });
          items.push({ label: t('订单详情') });
        } else if (path.startsWith('/stores/')) {
          items.push({ label: t('门店管理') });
          items.push({ label: t('门店列表'), path: '/stores' });
          items.push({ label: t('门店详情') });
        } else if (path === '/orders/create') {
          items.push({ label: t('订单中心') });
          items.push({ label: t('新建订单') });
        } else {
          items.push({ label: t('当前页面') });
        }
      }
    }

    return items;
  }, [path, t]);

  const breadcrumbItems = useMemo(() => {
    const items = [
      {
        title: (
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 4, color: isDark ? 'rgba(255,255,255,0.45)' : '#8c8c8c' }}>
            <HomeOutlined />
          </Link>
        ),
        key: 'home',
      },
    ];

    breadcrumbs.forEach((item, index) => {
      const isLast = index === breadcrumbs.length - 1;
      items.push({
        title: isLast || !item.path ? (
          <span style={{ color: isDark ? 'rgba(255,255,255,0.85)' : '#1f1f1f', fontWeight: isLast ? 500 : 400 }}>{t(item.label)}</span>
        ) : (
          <span onClick={() => navigate(item.path!)} style={{ cursor: 'pointer', color: isDark ? 'rgba(255,255,255,0.45)' : '#8c8c8c' }}>
            {t(item.label)}
          </span>
        ),
        key: item.label + index,
      });
    });

    return items;
  }, [breadcrumbs, navigate, t, isDark]);

  if (hidden) return null;

  return (
    <Breadcrumb
      items={breadcrumbItems}
      style={{ marginBottom: 16, fontSize: 'var(--font-sm)' }}
      separator=">"
    />
  );
};

export default BreadcrumbNav;
